import React from 'react';
import { Button } from '../../../ui/button';
import { BookOpen, Maximize2, Minimize2, Menu, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";

const HeaderToolbar = ({
  viewType,
  setViewType,
  displayMode,
  setDisplayMode,
  isCustomDateRange,
  setIsDateRangePickerOpen,
  setIsCustomDateRange,
  setCustomStartDate,
  setCustomEndDate,
  setEditingTraining,
  navigate,
  toggleFullscreen,
  isFullscreen,
  setSidebarOpen,
  activeFilters,
  onFiltersChange,
  scrollableCalendarStart,
  scrollableCalendarEnd,
  onNavigateBack,
  onNavigateForward,
  onNavigateToday
}) => {
  
  const getDisplayTitle = () => {
    // Show full date range
    const startDate = scrollableCalendarStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const endDate = scrollableCalendarEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${startDate} - ${endDate}`;
  };
  return (
    <div className="bg-gradient-to-r from-brand-50 to-steel-50 p-4">
      <div className="flex flex-col space-y-2">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="ml-4 items-start text-2xl font-bold text-slate-900 ">
            Trainings Panel
          </div>
          {viewType === 'trainings' && (
            <div className="flex items-center space-x-1 ml-4">
              {['table', 'calendar'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setDisplayMode(mode)}
                  className={`px-3 py-1 text-sm font-medium rounded-xl transition-colors ${
                    displayMode === mode
                      ? 'bg-brand-500 text-white shadow-sm shadow-brand-500/20'
                      : 'bg-white text-slate-600 hover:bg-steel-100 border border-steel-200'
                  }`}
                >
                  {mode === 'table' ? 'Table' : 'Calendar'}
                </button>
              ))}
            </div>
          )}
          <div className="md:flex-1 md:flex md:justify-center">
            <div className="flex items-center space-x-2">
              {/* Navigation buttons */}
              <button
                onClick={onNavigateBack}
                className="p-1.5 rounded-xl hover:bg-steel-200 transition-colors"
                title="Go back 30 days"
              >
                <ChevronLeft className="h-5 w-5 text-slate-600" />
              </button>

              <button
                onClick={onNavigateToday}
                className="px-3 py-1 text-sm font-medium rounded-xl bg-brand-100 text-brand-600 hover:bg-brand-200 transition-colors"
              >
                Today
              </button>

              <div className="flex items-center space-x-2">
                <h2 className="text-xl font-bold text-slate-800">{getDisplayTitle()}</h2>
                <select
                  className="px-2 py-1 pr-8 text-xs border border-steel-200 rounded-xl shadow-sm bg-white"
                  value={isCustomDateRange ? "custom" : "scrollable"}
                  onChange={(e) => {
                    if (e.target.value === "custom") {
                      setIsDateRangePickerOpen(true);
                    } else {
                      onNavigateToday();
                    }
                  }}
                >
                  <option value="scrollable">Default view</option>
                  <option value="custom">Custom range</option>
                </select>
              </div>

              <button
                onClick={onNavigateForward}
                className="p-1.5 rounded-xl hover:bg-steel-200 transition-colors"
                title="Go forward 30 days"
              >
                <ChevronRight className="h-5 w-5 text-slate-600" />
              </button>
            </div>
          </div>
          <div className="flex items-center space-x-4">

            {/* Search Input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-slate-400" />
              </div>
              <input
                type="text"
                placeholder={viewType === 'users' ? 'Search users...' : 'Search trainings...'}
                value={activeFilters.searchTerm || ''}
                onChange={(e) => onFiltersChange({ ...activeFilters, searchTerm: e.target.value })}
                className="block w-64 pl-10 pr-3 py-2 border border-steel-300 rounded-xl text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            
            {/* Add Training Button - visible in all modes */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditingTraining({ isNew: true })}
              className="shadow-sm flex items-center"
            >
              <BookOpen className="h-4 w-4 mr-1" />
              Add Training
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="shadow-sm h-8 w-8 p-0 ml-2 rounded-xl">
                  <Menu className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48 bg-white border border-steel-200 rounded-xl shadow-lg z-[1000]">
                <DropdownMenuItem
                  className="px-4 py-2 text-sm text-slate-700 hover:bg-brand-50 cursor-pointer"
                  onClick={() => navigate("/trainings/management")}
                >
                  Management
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="px-4 py-2 text-sm text-slate-700 hover:bg-brand-50 cursor-pointer"
                  onClick={() => navigate("/trainings/analysis")}
                >
                  Analysis
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={toggleFullscreen} className="shadow-sm h-8 w-8 p-0">
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            
            {/* Filters Button - moved to the end */}
            <button
              onMouseEnter={() => setSidebarOpen(true)}
              className="flex items-center bg-white rounded-xl border border-steel-200 shadow-sm px-3 py-2 text-sm hover:bg-steel-50 transition-colors ml-2"
            >
              <Menu className="h-4 w-4 mr-2 text-slate-400" />
              <span className="text-slate-700">
                Filters
                {(viewType === 'users'
                  ? (activeFilters.selectedRoles.length + activeFilters.selectedSites.length + activeFilters.selectedCountries.length +
                     activeFilters.selectedManagers.length + activeFilters.selectedSupervisors.length + activeFilters.selectedCoordinators.length)
                  : (activeFilters.selectedQualifications.length + activeFilters.selectedTrainingCenters.length + activeFilters.selectedCreatedBy.length + activeFilters.selectedStatuses.length)
                 ) > 0 && (
                  <span className="ml-1 bg-brand-100 text-brand-500 text-xs px-1.5 py-0.5 rounded-full">
                    {viewType === 'users'
                      ? (activeFilters.selectedRoles.length + activeFilters.selectedSites.length + activeFilters.selectedCountries.length +
                         activeFilters.selectedManagers.length + activeFilters.selectedSupervisors.length + activeFilters.selectedCoordinators.length)
                      : (activeFilters.selectedQualifications.length + activeFilters.selectedTrainingCenters.length + activeFilters.selectedCreatedBy.length + activeFilters.selectedStatuses.length)
                    }
                  </span>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderToolbar;